import classNames from "classnames";
import style from "../styles.module.scss";
import { useEffect, useState, useRef, useCallback } from "react";
import { Icon } from "@components/Icon/Icon";
import arrow from "@svg-icons/arrow-with-line-down.component.svg";
import { AspectRatio } from "@components/AspectRatio/AspectRatio";
import { UmbracoImage } from "@components/Image/UmbracoImage/UmbracoImage";
import { Text } from "@components/Text/Text";
import { data } from "../data";
import { spotProps } from "@modules/ArticleSpots/ArticleSpots";


interface ResultCardData {
	fine?: number; // Made optional since the component calculates fine internally
	speed: number;
	speedLimit: number;
	vehicle: string;
	roadType: string;
	conditions: string[];
	trailers?: string;
	roadwork?: boolean;
	displayTexts?: string[];
	isUbet?: boolean;
	overspeedPercentage?: number;
	formattedFine?: string;
	texts: {
		tabs: {
			url: string;
			label: string;
			text: string;
		}[];
	};
}

interface ResultCardProps {
	data: ResultCardData;
	colorCategory?: string; // Color category: 'green', 'yellow', or 'red'
	spotRibbons?: {
		GreenRibbon: SpotRibbon[];
		YellowRibbon: SpotRibbon[];
		RedRibbon: SpotRibbon[];
	}
}

interface SpotRibbon {
	Headline: string;
	Spots: spotProps[];
}

const ResultCard: React.FC<ResultCardProps> = ({ data: resultData, colorCategory = 'green', spotRibbons }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [selectedRibbon, setSelectedRibbon] = useState<SpotRibbon[] | undefined>(spotRibbons?.GreenRibbon || undefined);

	// Refs for focus management
	const drawerRef = useRef<HTMLDivElement>(null);
	const headerButtonRef = useRef<HTMLButtonElement>(null);
	const backdropRef = useRef<HTMLDivElement>(null);

	const closeDrawer = useCallback(() => {
		setIsExpanded(false);
		// Return focus to the header button when closing
		if (headerButtonRef.current) {
			headerButtonRef.current.focus();
		}
	}, []);

	const toggleDrawer = () => {
		const newExpandedState = !isExpanded;
		setIsExpanded(newExpandedState);

		// Focus management when opening
		if (newExpandedState && drawerRef.current) {
			// Focus the first focusable element in the drawer
			const firstFocusableElement = drawerRef.current.querySelector(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			) as HTMLElement;
			if (firstFocusableElement) {
				firstFocusableElement.focus();
			}
		}
	};

	// Handle escape key
	useEffect(() => {
		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && isExpanded) {
				closeDrawer();
			}
		};

		if (isExpanded) {
			document.addEventListener('keydown', handleEscapeKey);
		}

		return () => {
			document.removeEventListener('keydown', handleEscapeKey);
		};
	}, [isExpanded, closeDrawer]);

	// Handle click outside (on backdrop)
	const handleBackdropClick = (event: React.MouseEvent) => {
		if (event.target === backdropRef.current) {
			closeDrawer();
		}
	};

	// Handle click outside (anywhere outside the drawer)
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (isExpanded && drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
				closeDrawer();
			}
		};

		if (isExpanded) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isExpanded, closeDrawer]);

	// Trap focus within the drawer when expanded
	useEffect(() => {
		if (!isExpanded) return;

		const handleTabKey = (event: KeyboardEvent) => {
			if (event.key !== 'Tab') return;

			const focusableElements = drawerRef.current?.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);

			if (!focusableElements || focusableElements.length === 0) return;

			const firstElement = focusableElements[0] as HTMLElement;
			const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

			if (event.shiftKey) {
				if (document.activeElement === firstElement) {
					event.preventDefault();
					lastElement.focus();
				}
			} else {
				if (document.activeElement === lastElement) {
					event.preventDefault();
					firstElement.focus();
				}
			}
		};

		document.addEventListener('keydown', handleTabKey);
		return () => document.removeEventListener('keydown', handleTabKey);
	}, [isExpanded]);

	// Make all focusable elements in result content unfocusable when collapsed
	useEffect(() => {
		if (!drawerRef.current) return;

		const resultContent = drawerRef.current.querySelector(`.${style.resultContent}`);
		if (!resultContent) return;

		// Get all focusable elements including nested ones (like article cards)
		const focusableElements = resultContent.querySelectorAll(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
		);

		focusableElements.forEach((element) => {
			const htmlElement = element as HTMLElement;
			if (!isExpanded) {
				// Store original tabindex and set to -1
				const originalTabindex = htmlElement.getAttribute('tabindex') || '0';
				htmlElement.setAttribute('data-original-tabindex', originalTabindex);
				htmlElement.setAttribute('tabindex', '-1');
				// Also set aria-hidden for better accessibility
				htmlElement.setAttribute('aria-hidden', 'true');
			} else {
				// Restore original tabindex
				const originalTabindex = htmlElement.getAttribute('data-original-tabindex');
				if (originalTabindex) {
					htmlElement.setAttribute('tabindex', originalTabindex);
					htmlElement.removeAttribute('data-original-tabindex');
				} else {
					htmlElement.removeAttribute('tabindex');
				}
				// Remove aria-hidden
				htmlElement.removeAttribute('aria-hidden');
			}
		});
	}, [isExpanded]);

	// Format number with proper Danish thousand separators
	const formatPrice = (price: number): string => {
		if (price === 0) return "0";
		return price.toLocaleString('da-DK');
	};

	// Get the appropriate CSS class based on color category
	const getCardColorClass = () => {
		switch (colorCategory) {
			case 'yellow':
				return style.yellowCard;
			case 'red':
				return style.redCard;
			case 'green':
			default:
				return style.greenCard;
		}
	};

	useEffect(() => {
		if (colorCategory === 'green') {
			setSelectedRibbon(spotRibbons?.GreenRibbon);
		} else if (colorCategory === 'yellow') {
			setSelectedRibbon(spotRibbons?.YellowRibbon);
		} else if (colorCategory === 'red') {
			setSelectedRibbon(spotRibbons?.RedRibbon);
		}

	}, [colorCategory]);

	// Helper function to get default speed based on vehicle, road type, and trailer
	const getDefaultSpeed = (vehicle: string, roadType: string, trailer?: string): number => {
		const vehicleLimits = data.vehicleSpeedLimits[vehicle as keyof typeof data.vehicleSpeedLimits];
		if (!vehicleLimits) return 80; // fallback

		const roadLimits = vehicleLimits[roadType as keyof typeof vehicleLimits];
		if (!roadLimits) return 80; // fallback

		// Check if trailer affects the speed limit
		if (trailer && roadLimits[trailer as keyof typeof roadLimits]) {
			return (roadLimits[trailer as keyof typeof roadLimits] as any).default;
		}

		return roadLimits.default;
	};

	// Helper function to determine if it's an unconditional disqualification
	const isUnconditionalDisqualification = (speed: number, speedLimit: number, vehicle: string, trailer?: string): boolean => {
		const rules = data.resultCard.rules.isUbet;
		const percentageOver = (speed / speedLimit) * 100 - 100;

		// Speed threshold check
		if (speed >= rules.speedThreshold) {
			return true;
		}

		// Vehicle-specific rules
		const vehicleRules = rules.conditions[vehicle as keyof typeof rules.conditions];
		if (!vehicleRules) return false;

		if (vehicle === 'car' || vehicle === 'mc') {
			const hasTrailer = trailer && (trailer === 'trailer' || trailer === 'trailer-tempo100');
			const ruleSet = hasTrailer ? (vehicleRules as any).withTrailer : (vehicleRules as any).noTrailer;

			return percentageOver >= ruleSet.percentageOver && speed >= ruleSet.minSpeed;
		} else if (vehicle.startsWith('bus') || vehicle === 'truck') {
			return percentageOver >= (vehicleRules as any).percentageOver && speed >= (vehicleRules as any).minSpeed;
		}

		return false;
	};

	// Helper function to calculate fine based on rates from data.ts
	const calculateFine = (speed: number, speedLimit: number, vehicle: string, trailer?: string, roadwork?: boolean): number => {
		if (!speed || speed === 0) return 0;
		if (speed > data.resultCard.rules.speedLimits.maxSpeed) return 0; // Over speed limit
		if (speedLimit >= speed) return 0; // No fine if within limit

		const percentageOver = (speed / speedLimit) * 100 - 100;
		let fine = 0;

		// Find the appropriate rate based on percentage over
		for (let i = data.fineRates.length - 1; i >= 0; i--) {
			const rate = data.fineRates[i];
			if (percentageOver >= rate.percentage) {
				// Determine which rate to use based on vehicle and conditions
				if (speedLimit < 100 && (vehicle === 'mc' || vehicle === 'car') && !trailer) {
					fine = rate.rate1;
				} else if (vehicle.startsWith('bus') || vehicle === 'truck' ||
					((vehicle === 'mc' || vehicle === 'car') && (trailer === 'trailer' || trailer === 'trailer-tempo100'))) {
					fine = rate.rate3;
				} else if (speedLimit >= 100 && (vehicle === 'mc' || vehicle === 'car')) {
					fine = rate.rate2;
				}
				break;
			}
		}

		// High speed penalty (over 30% in city or country with speed limit <= 90)
		if ((resultData.roadType === 'city' ||
			(resultData.roadType === 'country' && speedLimit <= 90)) &&
			percentageOver >= 30) {
			fine += data.fineRates[0].rate1; // Add base rate
		}

		// Speed over 140 km/h penalty
		if (speed >= 140) {
			const timesOver = Math.floor((speed - 140) / 10);
			const additionalFine = timesOver * (data.fineRates[0].rate1 / 2) + data.fineRates[0].rate1;
			fine += additionalFine;
		}

		// Roadwork multiplier
		if (roadwork) {
			fine *= data.resultCard.rules.fineCalculation.roadworkMultiplier;
		}

		return fine;
	};

	// Helper function to get display texts based on rules
	const getDisplayTexts = (speed: number, speedLimit: number, vehicle: string, trailer?: string, roadwork?: boolean): string[] => {
		const displayTexts: string[] = [];
		const percentageOver = (speed / speedLimit) * 100 - 100;

		// Check vehicle-specific speed violations
		const vehicleSpecificRules = data.resultCard.rules.speedLimits.vehicleSpecific;
		if (vehicleSpecificRules[vehicle as keyof typeof vehicleSpecificRules]) {
			const rule = vehicleSpecificRules[vehicle as keyof typeof vehicleSpecificRules];
			if (speed > rule.maxSpeed) {
				displayTexts.push(rule.displayText);
			}
		}

		// Check display text rules
		const displayRules = data.resultCard.rules.displayTexts;

		// Check for "betkor" conditions
		const betkorConditions = displayRules.betkor.conditions;
		let shouldShowBetkor = false;

		for (const condition of betkorConditions) {
			if (condition.roadwork && roadwork && percentageOver > condition.percentageOver) {
				shouldShowBetkor = true;
				break;
			}

			if (condition.vehicles && condition.vehicles.includes(vehicle) && percentageOver > condition.percentageOver) {
				shouldShowBetkor = true;
				break;
			}

			if (condition.withTrailer && trailer && percentageOver > condition.percentageOver) {
				shouldShowBetkor = true;
				break;
			}

			if (condition.noTrailer && !trailer && percentageOver > condition.percentageOver) {
				shouldShowBetkor = true;
				break;
			}

			if (condition.orSpeedThreshold && speed >= condition.orSpeedThreshold) {
				shouldShowBetkor = true;
				break;
			}
		}

		if (shouldShowBetkor) {
			displayTexts.push("betkor");
		} else if (percentageOver > displayRules.klip.percentageOver) {
			displayTexts.push("klip");
		}

		return displayTexts;
	};

	// Get display text for conditions and speed details
	const getViolationSummary = () => {
		if (!resultData) return data.resultCard.fallbackText;

		const parts = [`${resultData.speed} km/t`];

		// Map road types using data from JSON
		const roadTypeMapping = data.resultCard.mapping.roadTypes;
		if (roadTypeMapping[resultData.roadType as keyof typeof roadTypeMapping]) {
			parts.push(roadTypeMapping[resultData.roadType as keyof typeof roadTypeMapping]);
		}

		// Map vehicles using data from JSON
		const vehicleMapping = data.resultCard.mapping.vehicles;
		if (vehicleMapping[resultData.vehicle as keyof typeof vehicleMapping]) {
			parts.push(vehicleMapping[resultData.vehicle as keyof typeof vehicleMapping]);
		}

		// Map conditions using data from JSON
		const conditionMapping = data.resultCard.mapping.conditions;
		resultData.conditions.forEach(condition => {
			if (conditionMapping[condition as keyof typeof conditionMapping]) {
				parts.push(conditionMapping[condition as keyof typeof conditionMapping]);
			}
		});

		return parts.join(' + ');
	};

	// Determine if this should be an unconditional disqualification
	const shouldBeUbet = isUnconditionalDisqualification(
		resultData.speed,
		resultData.speedLimit,
		resultData.vehicle,
		resultData.trailers
	);

	// Check if roadwork condition is active
	const hasRoadwork = resultData.conditions.includes('roadwork');

	// Calculate the fine using the rates from data.ts
	const calculatedFine = calculateFine(
		resultData.speed,
		resultData.speedLimit,
		resultData.vehicle,
		resultData.trailers,
		hasRoadwork
	);

	// Get display texts based on rules (including roadwork)
	const displayTexts = getDisplayTexts(
		resultData.speed,
		resultData.speedLimit,
		resultData.vehicle,
		resultData.trailers,
		hasRoadwork
	);

	useEffect(() => {
		if (isExpanded) {
			// Get proper labels for the values
			const zoneLabel = data.resultCard.mapping.roadTypes[resultData.roadType as keyof typeof data.resultCard.mapping.roadTypes] || resultData.roadType;
			const vehicleLabel = data.resultCard.mapping.vehicles[resultData.vehicle as keyof typeof data.resultCard.mapping.vehicles] || resultData.vehicle;
			const conditionsLabels = resultData.conditions.map(condition =>
				data.resultCard.mapping.conditions[condition as keyof typeof data.resultCard.mapping.conditions] || condition
			).join(', ');

			(window as any)['dataLayer'] = (window as any)['dataLayer'] || [];
			(window as any)['dataLayer'].push({
				event: 'fine_calculator',
				action: 'Vis konsekvenser',
				speed: resultData.speed,
				zone: zoneLabel,
				vehicle: vehicleLabel,
				other: conditionsLabels,
				expense: shouldBeUbet ? data.resultCard.ui.unconditionalDisqualification : calculatedFine
			});
		}
	}, [isExpanded]);

	return (
		<>
			<div
				ref={backdropRef}
				className={`${style.cardContainerBackdrop} ${!isExpanded ? style.visuallyHidden : ""}`.trim()}
				onClick={handleBackdropClick}
				aria-hidden={!isExpanded}
			></div>
			<div
				ref={drawerRef}
				className={classNames(style.cardContainer, getCardColorClass(), `${isExpanded ? style.expanded : ""}`.trim())}
				role="dialog"
				aria-modal={isExpanded}
				aria-labelledby="drawer-header"
				aria-describedby="drawer-content"
			>
				{/* Collapsed Header */}
				<button
					ref={headerButtonRef}
					className={style.drawerHeader}
					onClick={toggleDrawer}
					aria-expanded={isExpanded}
					aria-controls="drawer-content"
					id="drawer-header"
				>
					<div className={style.headerContent}>
						<span className={style.headerText}>
							{calculatedFine === 0 ? data.resultCard.ui.headerTexts.noFine : data.resultCard.ui.headerTexts.withFine}
						</span>
						<span className={style.headerSubtext}>{getViolationSummary()}</span>
					</div>
					<div className={style.priceSection}>
						<span className={classNames(style.price, shouldBeUbet && style.isUbet)}>
							{shouldBeUbet ? data.resultCard.ui.unconditionalDisqualification : formatPrice(calculatedFine)}
						</span>
						<span className={classNames(style.currency, shouldBeUbet && style.hidden)}>
							{data.resultCard.ui.currency}
						</span>
					</div>
					<div className={style.expandButtonContainer}>
						<div className={style.expandButton}>
							<span>
								{isExpanded ? data.resultCard.ui.buttonTexts.close : data.resultCard.ui.buttonTexts.expand}
							</span>
							<span className={`${style.expandArrowIcon} ${isExpanded ? style.expandArrowIconExpanded : ""}`.trim()}>
								<Icon svg={arrow} />
							</span>
						</div>
					</div>
				</button>

				{/* Expanded Content */}
				<div
					className={`${style.resultContent} ${style.drawerContent}`.trim()}
					id="drawer-content"
					aria-hidden={!isExpanded}
					tabIndex={isExpanded ? undefined : -1}
				>
					{(() => {
						// Check if speed is in red zone (all text only appears in red zone)
						const isInRedZone = colorCategory === 'red';

						// Only show any text if speed is in red zone
						if (!isInRedZone) {
							return null;
						}

						// Check if trial period condition is selected
						const hasTrialPeriod = resultData.conditions.includes('probation');
						// Check if it's an unconditional disqualification (ubet)
						const isUbet = shouldBeUbet;
						// Check if bus is selected
						const isBus = resultData.vehicle === 'bus';
						const isBus100 = resultData.vehicle === 'bus100';

						// Rule 1: hastrialperiod || (isredzone && not ubet) => trialperiod text or the unconditionaldisqualification text
						if ((hasTrialPeriod || !isUbet) && !isUbet) {
							return (
								<div>
									<h2>
										{hasTrialPeriod
											? data.resultCard.ui.probationPeriodTitle
											: data.resultCard.ui.unconditionalDisqualificationTitle
										}
									</h2>
									<ul>
										{(hasTrialPeriod
											? data.resultCard.ui.probationPeriodText
											: data.resultCard.ui.unconditionalDisqualificationText
										).map((text: string, index: number) => (
											<li key={index}>{text}</li>
										))}
									</ul>
								</div>
							);
						}

						// Rule 2: ubet && trialperiod => ubet text and trial period text
						if (isUbet && hasTrialPeriod) {
							return (
								<>
									<div className={style.resultContentInfo}>
										{data.resultCard.ui.ubetText.map((text: string, index: number) => (
											<p key={index}>{text}</p>
										))}
									</div>
									<div>
										<h2>{data.resultCard.ui.probationPeriodTitle}</h2>
										<ul>
											{data.resultCard.ui.probationPeriodText.map((text: string, index: number) => (
												<li key={index}>{text}</li>
											))}
										</ul>
									</div>
								</>
							);
						}

						// Rule 3: ubet => ubet text
						if (isUbet && !hasTrialPeriod) {
							return (
								<div className={style.resultContentInfo}>
									{data.resultCard.ui.ubetText.map((text: string, index: number) => (
										<p key={index}>{text}</p>
									))}

								</div>
							);
						}

						// Return null if no conditions are met
						return null;
					})()}

					{/* Bus-specific information */}
					{(() => {
						// Only show bus info if speed is in red zone
						const isInRedZone = colorCategory === 'red';
						if (!isInRedZone) {
							return null;
						}

						const isUbet = shouldBeUbet;
						const isBus = resultData.vehicle === 'bus';
						const isBus100 = resultData.vehicle === 'bus100';

						// Rule 4: ubet && bus => ubet text and bus text
						if (isBus && isInRedZone) {
							return (
								<div className={style.resultContentInfo}>
									{data.resultCard.ui.busInfo.map((text: string, index: number) => (
										<p key={index}><strong>{text}</strong></p>
									))}
								</div>
							);
						}

						// Rule 5: ubet && bus100 => ubet text and bus100 text
						if (isBus100 && isInRedZone) {
							return (
								<div className={style.resultContentInfo}>
									{data.resultCard.ui.busTempo100Info.map((text: string, index: number) => (
										<p key={index}><strong>{text}</strong></p>
									))}
								</div>
							);
						}

						return null;
					})()}

					{selectedRibbon && selectedRibbon.length > 0 && selectedRibbon[0].Spots.length > 0 && (
						<div className={style.ribbonContainer}>
							<h2>
								{selectedRibbon[0].Headline}
							</h2>
							<div className={style.spotContainer}>
								{selectedRibbon[0].Spots.map((spot, index) => (
									<a
										key={index}
										className={style.spotLink}
										href={typeof spot.Link === "string" ? spot.Link : spot.Link?.Url}
										target="_blank"
										rel="noopener noreferrer"
									>
										<AspectRatio ratio={"16:9"}>
											<UmbracoImage removeLazyLoad image={spot.Image} width={100} height={100} />
										</AspectRatio>
										<Text className={colorCategory === "red" ? style.whiteText : ""}>{spot.Headline}</Text>
									</a>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default ResultCard;
